"use client";

import { DateRange, DayPicker } from "react-day-picker";
import { arSA, enUS } from "date-fns/locale";
import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Button, buttonVariants } from "@/components-shared/ui/button";
import { cn, roundTo1Decimal } from "@/lib/utils";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/redux/store";
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  format,
  isBefore,
  isSameDay,
  subDays,
} from "date-fns";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import config from "@/lib/config";
import { useParams, useRouter } from "next/navigation";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { dir } from "i18next";
import { useLocale, useTranslations } from "next-intl";
import DayButton from "./Day";
import { Skeleton } from "@/components-shared/ui/skeleton";
import useOnMonthChange from "../utils/useOnMonthChange";
import CircularProgress from "@/components-shared/CircularProgress";
import { toggleDrawer } from "@/redux/slices/drawer.slice";
import { Input } from "@/components-shared/ui/input";
import { CheckCircle, XCircle } from "lucide-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { event } from "@/lib/gtag";

const availabilitySchema = yup
  .object({
    date: yup.object({
      from: yup
        .mixed<Date | string>()
        .test(
          "is-string-or-date",
          "Must be a string or date",
          (value) => typeof value === "string" || value instanceof Date
        )
        .required(),
      to: yup
        .mixed<Date | string>()
        .test(
          "is-string-or-date",
          "Must be a string or date",
          (value) => typeof value === "string" || value instanceof Date
        )
        .required(),
    }),
  })
  .test({
    test: (data) => !!data.date.from && !!data.date.to,
  });

const ReservationDuration = ({
  className,
  total,
  original,
  initStep,
  bookingId,
  from,
  unitName,
  unitCode,
  to,
  onPromoCodeChange,
  initialPromoCodeData,
  initialPromoCodeName,
}: {
  className?: string;
  total: string;
  original: string;
  insurance?: string;
  initStep?: number;
  from?: Date;
  to?: Date;
  bookingId?: string;
  refundableInsurance?: boolean;
  unitName?: string;
  unitCode?: string;
  onPromoCodeChange?: (promoName: string) => void;
  initialPromoCodeData?: any;
  initialPromoCodeName?: string;
}) => {
  const { unit } = useParams();
  const locale = useLocale();
  const t = useTranslations();
  const [duration, setDuration] = useState<number | undefined>(0);
  const { isLoggedIn } = useAppSelector((state) => state.login);
  const [estimation, setEstimation] = useState<EstimationType>(null);
  const [isGettingEstimation, setIsGettingEstimation] = useState(false);
  const [step, setStep] = useState<number>(initStep || 1);
  const [selectionErr, setSelectionErr] = useState<boolean>(false);
  const [selectionErrMsg, setSelectionErrMsg] = useState<string>("");
  const [promoCode, setPromoCode] = useState<string>(
    initialPromoCodeName || ""
  );
  const [promoCodeData, setPromoCodeData] = useState<any>(
    initialPromoCodeData || null
  );
  const [promoError, setPromoError] = useState<string>("");
  const [promoApplied, setPromoApplied] = useState(!!initialPromoCodeData);
  const dispatch = useDispatch();
  const days = useAppSelector((state) => state.monthPrices);
  const onMonthChange = useOnMonthChange(
    from ? new Date(from) : new Date(),
    bookingId
  );
  const router = useRouter();

  const bookingForm = useForm<{
    date: { from: string | Date; to: string | Date };
  }>({
    mode: "all",
    defaultValues: { date: { from, to } },
    resolver: yupResolver(availabilitySchema),
  });

  const unitId = useMemo(() => unit as string, [unit]);
  const fromDate = bookingForm.watch("date.from");
  const toDate = bookingForm.watch("date.to");
  const selected = bookingForm.watch("date");
  const priceOfToday = useMemo(
    () => ({ original: Number(original), current: Number(total) }),
    [original, total]
  );

  // Sync promo code data with parent component
  useEffect(() => {
    if (promoApplied) {
      onPromoCodeChange?.(promoCode);
    }
  }, [promoCode, promoApplied, onPromoCodeChange]);

  async function goToCreateBooking(values: any) {
    if (!isLoggedIn) {
      dispatch(toggleDrawer({ isOpen: true }));
      return;
    }
    const query = {
      checkInDate: format(new Date(values?.date?.from), "yyyy-MM-dd"),
      checkOutDate: isSameDay(
        new Date(values?.date?.from),
        new Date(values?.date?.to)
      )
        ? format(addDays(new Date(values?.date?.from), 1), "yyyy-MM-dd")
        : format(new Date(values?.date?.to), "yyyy-MM-dd"),
      ...(promoApplied &&
        promoCodeData?.promoCodeId && {
          promoCodeId: promoCodeData.promoCodeId,
          promoCodeName: promoCode,
        }),
    };

    if (bookingId) Object.assign(query, { bookingId });

    if ((bookingId && estimation?.action === "PAYMENT") || !bookingId)
      router.push(
        `/all-units/${unitId}/checkout?${new URLSearchParams(query).toString()}`
      );
    else {
      try {
        await ehjzleeInstance.put(
          `${config.props}/${unitId}/bookings/${bookingId}`,
          {
            checkInDate: format(new Date(values.date.from), "yyyy-MM-dd"),
            checkOutDate: format(new Date(values.date.to), "yyyy-MM-dd"),
            isFullyPaid: false,
            ...(promoApplied &&
              promoCodeData?.promoCodeId && {
                promoCodeId: promoCodeData.promoCodeId,
                promoCodeName: promoCode,
              }),
          }
        );
        toast.success(t("reservationDuration.bookingUpdated"));
        router.push(`/reservations/${bookingId}`);
      } catch (error: any) {
        console.log(error);
        toast.error(t(error.response.data.message));
      }
    }
  }

  const getEstimation = useCallback(
    async (
      date: { from: Date | string; to?: Date | string },
      bookingId?: string,
      promoCodeName?: string
    ) => {
      try {
        setIsGettingEstimation(true);
        const pricesRes = await ehjzleeInstance.post(
          `${config.props}/${unitId}/estimation${
            bookingId ? `/${bookingId}` : ""
          }`,
          {
            checkInDate: format(new Date(date.from!), "yyyy-MM-dd"),
            checkOutDate: format(date.to!, "yyyy-MM-dd"),
            ...(promoCodeName && { promoCodeName }),
          }
        );
        setEstimation(pricesRes.data.estimate);

        // Handle promo code response
        if (promoCodeName && pricesRes.data.estimate?.promoCodeId) {
          setPromoCodeData({
            promoCodeId: pricesRes.data.estimate.promoCodeId,
            promoDiscount: pricesRes.data.estimate.promoDiscount,
          });
          setPromoApplied(true);
          setPromoError("");
        } else if (promoCodeName) {
          setPromoError(t("promo.not.valid"));
          setPromoApplied(false);
        }
      } catch (error: any) {
        console.log(error);
        if (promoCodeName) {
          setPromoError(
            t(`${error.response?.data?.message}`) || t("promoCode.error")
          );
          setPromoApplied(false);
        }
      } finally {
        setIsGettingEstimation(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitId, t]
  );

  const removePromoCode = useCallback(() => {
    setPromoCode("");
    setPromoCodeData(null);
    setPromoApplied(false);
    setPromoError("");
    onPromoCodeChange?.("");

    // Trigger new estimation without promo code
    if (fromDate && toDate) {
      getEstimation(
        {
          from: fromDate,
          to: toDate,
        },
        bookingId
      );
    }
  }, [onPromoCodeChange, fromDate, toDate, bookingId, getEstimation]);

  const handleDuration = useCallback((start: Date, end: Date) => {
    setDuration(
      differenceInDays(
        format(new Date(end), "yyyy-MM-dd"),
        format(new Date(start), "yyyy-MM-dd")
      ) || 1
    );
  }, []);

  const disabledDays = useMemo(
    () => {
      const disabled = days.daysInMonth
        ?.filter((day) => !day.isAvailable)
        ?.map((day) => new Date(day.date)) || [];

      console.log("Days from Redux:", days.daysInMonth);
      console.log("Disabled days:", disabled);

      return disabled;
    },
    [days?.daysInMonth]
  );

  // const timeBasedDisabledDate = useMemo(() => {
  //   const now = new Date();
  //   // Get today's date in local timezone, then set to start of day
  //   const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  //   const cutoffDate = now.getHours() < 5
  //     ? today // Before 5 AM: disable from today onwards (enable up to yesterday)
  //     : subDays(today, 1); // After 5 AM: disable up to yesterday (enable today and future)

  //   console.log("Current time:", now);
  //   console.log("Current hour:", now.getHours());
  //   console.log("Today (start of day):", today);
  //   console.log("Cutoff date:", cutoffDate);
  //   console.log("Logic:", now.getHours() < 5 ? "Before 5 AM - disable from today" : "After 5 AM - disable up to yesterday");

  //   return cutoffDate;
  // }, []);

  const handleDateSelection = useCallback(
    (dateRange: DateRange | undefined) => {
      if (!dateRange?.from) return;
      let { from, to } = dateRange;

      if (fromDate && toDate) {
        if (isBefore(from, fromDate)) {
          bookingForm.setValue(
            "date",
            { from, to: "" },
            { shouldValidate: true }
          );
        } else
          bookingForm.setValue(
            "date",
            { from: to!, to: "" },
            { shouldValidate: true }
          );
      } else {
        bookingForm.setValue(
          "date",
          {
            from,
            to:
              to && from && !isSameDay(new Date(to), new Date(from)) ? to : "",
          },
          { shouldValidate: true }
        );
      }
    },
    [toDate, fromDate]
  );

  const handleDisabledDays = useCallback(
    (start: Date, end: Date) => {
      const allDates = eachDayOfInterval({
        start,
        end: isSameDay(start, end) ? start : subDays(end, 1),
      });

      const intersection = allDates.filter((day) =>
        disabledDays.find((date) => isSameDay(day, date))
      );

      if (intersection.length && step !== 3) {
        setSelectionErrMsg(t("reservationDetails.selectionError"));
        setSelectionErr(true);
        setStep(1);
      } else {
        setSelectionErr(false);
      }
    },
    [disabledDays, step]
  );

  useEffect(() => {
    if (fromDate && toDate) {
      handleDisabledDays(new Date(fromDate), new Date(toDate));
    }
  }, [fromDate, toDate, handleDisabledDays]);

  const processDurationEstimation = useCallback(async () => {
    if (fromDate) {
      await getEstimation(
        {
          from: fromDate,
          to:
            toDate && !isSameDay(fromDate, toDate)
              ? toDate
              : addDays(fromDate, 1),
        },
        bookingId
      );
      handleDuration(new Date(fromDate), new Date(toDate || fromDate));
    }
  }, [fromDate, toDate, bookingId]);

  useEffect(() => {
    processDurationEstimation();
  }, [processDurationEstimation]);

  useEffect(() => {
    setStep(initStep ? initStep : from && to ? 2 : 1);
    if (from && to) bookingForm.reset({ date: { from, to } });
    else bookingForm.reset();

    // Initialize promo code data from props
    if (initialPromoCodeData) {
      setPromoCodeData(initialPromoCodeData);
      setPromoApplied(true);
      if (initialPromoCodeName) {
        setPromoCode(initialPromoCodeName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPromoCodeData, initialPromoCodeName]);

  const promoDiscount = useMemo(() => {
    return estimation?.promoDiscount ? Number(estimation.promoDiscount) : 0;
  }, [promoApplied, promoCodeData, estimation?.promoDiscount]);

  return (
    <div
      className={cn(
        "relative bg-white min-w-[352px] max-w-sm max-sm:max-w-full flex-1 shadow-input rounded-b-[65rem] rounded-t-full px-3 sm:px-6 pb-6 pt-20",
        className
      )}
    >
      <div className="absolute shadow-inner size-12 top-5 rounded-full left-1/2 -translate-x-1/2" />
      <div className="p-5 flex items-center justify-center gap-2 border-b border-green-main">
        {(priceOfToday?.current < priceOfToday?.original ||
          (estimation?.subtotalBeforeDiscounts &&
            estimation?.subtotalAfterDiscounts &&
            Number(estimation.subtotalBeforeDiscounts) >
              Number(estimation.subtotalAfterDiscounts))) && (
          <del className="font-light max-sm:text-sm text-gray-light">
            {!isGettingEstimation ? (
              estimation?.subtotalBeforeDiscounts && fromDate && toDate ? (
                roundTo1Decimal(
                  Number(estimation?.subtotalBeforeDiscounts)
                ).toLocaleString("en-US")
              ) : (
                roundTo1Decimal(priceOfToday.original).toLocaleString("en-US")
              )
            ) : (
              <Skeleton className="w-10 h-6" />
            )}
          </del>
        )}
        <strong className="text-green-main text-xl sm:text-4xl">
          {!isGettingEstimation ? (
            estimation?.subtotalAfterDiscounts && fromDate && toDate ? (
              roundTo1Decimal(
                Number(estimation?.subtotalAfterDiscounts) -
                  Number(promoDiscount)
              ).toLocaleString("en-US")
            ) : (
              roundTo1Decimal(priceOfToday.current).toLocaleString("en-US")
            )
          ) : (
            <Skeleton className="w-16 h-10" />
          )}
        </strong>
        <span className="max-sm:text-sm">
          {fromDate && toDate
            ? t("reservationDetails.currency")
            : t("reservationDuration.total")}
        </span>
      </div>

      {step === 1 ? (
        <>
          <p className="pt-6 pb-2 text-sm text-center">
            {fromDate && toDate
              ? t("bookingDate", {
                  from: format(new Date(fromDate), "dd/MM"),
                  to: format(
                    isSameDay(new Date(toDate), new Date(fromDate))
                      ? addDays(new Date(fromDate), 1)
                      : new Date(toDate),
                    "dd/MM"
                  ),
                  nights: `${duration} ${
                    duration! > 1 ? t("nights") : t("night")
                  }`,
                })
              : t("reservationDuration.selectDates")}
          </p>
          {selectionErr && (
            <p className="text-error text-sm text-center pb-6 max-w-[300px]">
              {selectionErrMsg}
            </p>
          )}
          <DayPicker
            required={false}
            classNames={{
              months:
                "flex flex-col relative sm:flex-row gap-y-4 sm:gap-x-4 sm:gap-y-0",
              month: "space-y-4",
              month_caption: "flex justify-start pt-1 relative items-center",
              caption_label: "hidden",
              nav: "flex absolute top-1.5 rtl:left-2 ltr:right-2 z-[10]",
              button_next: "rtl:rotate-180",
              button_previous: "rtl:rotate-180",
              month_grid: "w-full border-collapse space-y-1",
              weekdays: "flex justify-center gap-x-1",
              weekday:
                "text-neutral-500 rounded-md w-10 font-normal text-[0.7rem] dark:text-neutral-400",
              week: "flex justify-center w-full mt-2 gap-x-1",
              day: cn(
                buttonVariants({ variant: "ghost" }),
                "font-normal aria-selected:opacity-100 !rounded size-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-neutral-100/50 [&:has([aria-selected])]:bg-neutral-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-neutral-800/50 dark:[&:has([aria-selected])]:bg-neutral-800"
              ),
              selected:
                "bg-green-main text-neutral-50 hover:!bg-green-main/90 hover:text-neutral-50 focus:bg-green-main focus:text-neutral-50 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50 dark:hover:text-neutral-900 dark:focus:bg-neutral-50 dark:focus:text-neutral-900",
              outside:
                "day-outside text-neutral-500 opacity-50 aria-selected:bg-neutral-100/50 aria-selected:text-neutral-500 aria-selected:opacity-30 dark:text-neutral-400 dark:aria-selected:bg-neutral-800/50 dark:aria-selected:text-neutral-400",
              range_middle:
                "aria-selected:!bg-green-main/70 aria-selected:text-white dark:aria-selected:bg-neutral-800 dark:aria-selected:text-neutral-50",
              hidden: "invisible",
              range_start: "!bg-green-main !text-white",
              range_end: "!bg-green-main !text-white",
            }}
            dir={dir(locale)}
            mode="range"
            locale={locale === "ar" ? arSA : enUS}
            components={{
              DayButton: (props) => <DayButton {...props} />,
            }}
            endMonth={new Date(new Date().getFullYear() + 25, 11)}
            onSelect={handleDateSelection}
            selected={{
              from: selected?.from ? new Date(selected.from) : undefined,
              to: selected?.to ? new Date(selected.to) : undefined,
            }}
            onMonthChange={onMonthChange}
            captionLayout="dropdown"
            disabled={(date) => {
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const yesterday = subDays(today, 1);
              const shouldDisable = now.getHours() < 5
                ? date < yesterday // Before 5 AM: disable before yesterday (enable yesterday, today, and future)
                : date <= yesterday; // After 5 AM: disable yesterday and before (enable today and future)

              return shouldDisable || disabledDays.some(disabledDay =>
                disabledDay.getTime() === date.getTime()
              );
            }}
            defaultMonth={fromDate ? new Date(fromDate) : new Date()}
          />
        </>
      ) : (
        <div className="text-[#303E48] font-light space-y-3">
          {step === 2 && (
            <div className="flex justify-end mt-3">
              <Button
                variant={"link"}
                className="!h-auto p-0 underline text-xs text-end"
                onClick={() => setStep(1)}
              >
                {t("reservationDuration.dateReset")}
              </Button>
            </div>
          )}
          <section className="w-full flex *:flex-1 gap-x-2 font-alex mt-3">
            <div className="py-1 px-2 text-sm border rounded-2xl">
              <span className="text-[10px]">
                {t("reservationDuration.checkInDate")}
              </span>
              <p className="font-normal">
                {fromDate
                  ? format(new Date(fromDate), "dd LLLL y", {
                      locale: locale === "ar" ? arSA : enUS,
                    })
                  : from
                  ? format(from, "dd LLLL y", {
                      locale: locale === "ar" ? arSA : enUS,
                    })
                  : "-----------"}
              </p>
            </div>
            <div className="py-1 px-2 text-sm border rounded-2xl">
              <span className="text-[10px]">
                {t("reservationDuration.checkOutDate")}{" "}
              </span>
              <p className="font-normal">
                {toDate
                  ? format(
                      !isSameDay(fromDate, toDate)
                        ? new Date(toDate)
                        : addDays(new Date(fromDate), 1),
                      "dd LLLL y",
                      {
                        locale: locale === "ar" ? arSA : enUS,
                      }
                    )
                  : to
                  ? format(to, "dd LLLL y", {
                      locale: locale === "ar" ? arSA : enUS,
                    })
                  : "-----------"}
              </p>
            </div>
          </section>

          {/* Promo Code Section - moved from step 2 to step 3 */}
          {step === 3 && !promoApplied && (
            <div className="space-y-2">
              <div className="divider"></div>
              <div className="flex gap-2">
                <Input
                  placeholder={t("promoCode.placeholder")}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="flex-1 h-11"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      getEstimation(
                        {
                          from: fromDate,
                          to: toDate,
                        },
                        bookingId,
                        promoCode
                      );
                    }
                  }}
                />
                <Button
                  onClick={() =>
                    getEstimation(
                      {
                        from: fromDate,
                        to: toDate,
                      },
                      bookingId,
                      promoCode
                    )
                  }
                  disabled={!promoCode.trim() || isGettingEstimation}
                  className="shrink-0 h-11"
                >
                  {isGettingEstimation ? (
                    <CircularProgress />
                  ) : (
                    t("promoCode.apply")
                  )}
                </Button>
              </div>
              {promoError && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <XCircle className="size-3" />
                  {promoError}
                </p>
              )}
            </div>
          )}

          {/* Applied Promo Code Display */}
          {step === 3 && promoApplied && promoCodeData && (
            <div className="space-y-2">
              <div className="divider"></div>
              <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="size-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    {promoCode}
                  </span>
                </div>
                <Button
                  onClick={removePromoCode}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-800 !h-auto p-1"
                >
                  ✕
                </Button>
              </div>
            </div>
          )}

          <div className="divider"></div>
          <div className=" flex justify-between">
            <span>{t("reservationDuration.duration")} </span>
            <span>
              {duration} {duration! > 1 ? t("nights") : t("night")}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{t("reservationDuration.price")}</span>
            <span>
              {Number(estimation?.subtotalAfterDiscounts).toLocaleString(
                "en-US"
              )}{" "}
              {t("reservationDuration.currency")}
            </span>
          </div>
          <div className=" flex justify-between">
            <span>{t("reservationDuration.serviceFee")}</span>
            <span>
              {Number(estimation?.serviceFee).toLocaleString("en-US")}{" "}
              {t("reservationDuration.currency")}
            </span>
          </div>
          {(promoApplied && promoDiscount > 0) ||
          (estimation?.promoDiscount &&
            Number(estimation.promoDiscount) > 0) ? (
            <div className="flex justify-between text-green-600">
              <span>{t("promoCode.discount")}</span>
              <span>
                -
                {Number(
                  estimation?.promoDiscount || promoDiscount
                ).toLocaleString("en-US")}{" "}
                {t("reservationDuration.currency")}
              </span>
            </div>
          ) : null}
          <div className="divider"></div>
          <div className="font-black flex gap-12 justify-between">
            <span>{t("reservationDuration.totalAmount")}</span>
            <span>
              {Number(estimation?.totalPriceAfterFees).toLocaleString("en-US")}{" "}
              {t("reservationDuration.currency")}{" "}
            </span>
          </div>
          {estimation?.paidBefore && (
            <div className="font-black flex gap-12 justify-between">
              <span>{t("reservationDuration.paid")}</span>
              <span>
                {Number(estimation?.paidBefore).toLocaleString("en-US")}{" "}
                {t("reservationDuration.currency")}
              </span>
            </div>
          )}
          <div className="font-black flex gap-12 justify-between">
            <span>{t("reservationDuration.amountDueNow")}</span>
            <span>
              {Number(estimation?.depositAfterFees || 0).toLocaleString(
                "en-US"
              )}{" "}
              {t("reservationDuration.currency")}
            </span>
          </div>
        </div>
      )}
      {step !== 3 && (
        <Button
          disabled={
            bookingForm.formState.isSubmitting ||
            !bookingForm.formState.isValid ||
            selectionErr
          }
          onClick={
            step === 1
              ? () => {
                  setStep(2);
                event({
                  action: "add_to_cart",
                  properties: {
                    item_id: unitCode,
                    item_name: unitName,
                    item_value: Number(estimation?.totalPriceAfterFees),
                    currency: "SAR",
                  },
                });
                }
              : bookingForm.handleSubmit(goToCreateBooking)
          }
          className="w-full bg-green-main hover:bg-green-500 mt-6"
        >
          {step === 1 ? (
            t("reservationDuration.bookNow")
          ) : bookingForm.formState.isSubmitting ? (
            <CircularProgress />
          ) : (
            t("reservationDuration.confirmBooking")
          )}
        </Button>
      )}
    </div>
  );
};

export default memo(ReservationDuration);
